'use client';

import { useDictionary } from '@/components/providers';
import { interpolate } from '@/lib/i18n/interpolate';
import type { ConsentService } from './consent.types';
import { ConsentToggle } from './ConsentToggle';

/** Project palette — the only lines that differ between the sibling projects. */
const CLS = {
    label: 'text-sm font-medium text-title',
    description: 'mt-0.5 text-sm text-copy-2',
};

type Props = {
    service: ConsentService;
    checked: boolean;
    /** Services of a required category are shown as permanently on. */
    disabled: boolean;
    onChange: (checked: boolean) => void;
    toggleId: string;
};

/** One technology inside a category, with its own switch. */
export function ConsentServiceItem({ service, checked, disabled, onChange, toggleId }: Props) {
    const { consent } = useDictionary();

    // A service without dictionary copy falls back to its id. Visible and ugly on
    // purpose: silently dropping a consent-relevant service would be worse than
    // showing an untranslated one.
    const copy = consent.services[service.id] ?? { label: service.id, description: '' };

    return (
        <li className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
                <label htmlFor={toggleId} className={CLS.label}>
                    {copy.label}
                </label>
                {copy.description ? <p className={CLS.description}>{copy.description}</p> : null}
            </div>

            <ConsentToggle
                id={toggleId}
                checked={checked}
                disabled={disabled}
                label={interpolate(consent.banner.allowToggle, { name: copy.label })}
                onChange={onChange}
            />
        </li>
    );
}
